import {useMemo} from "react";
import {Autocomplete, Box, TextField} from "@mui/material";
import {useSnapshot} from "valtio";
import {appStore} from "@store/appStore";

export interface OrgOption {
    id: number;
    name: string;
    depth: number;
    hasChildren: boolean;
}

interface FlatOrg {
    id: number;
    name: string;
    parent_id: number | null;
}

/**
 * Build an ordered, indented list from the user's permitted orgs.
 * Orgs whose parent is also in the permitted set are nested under them;
 * orgs whose parent is NOT permitted become roots (no greyed-out ancestors).
 */
function buildOrgOptions(orgs: FlatOrg[], root?: {id: number; name: string}, pinFirst?: string): OrgOption[] {
    orgs = orgs.filter((o) => (o.name ?? "").trim() !== "");
    const byId = new Map<number, FlatOrg>();
    for (const o of orgs) byId.set(o.id, o);

    const childrenMap = new Map<number | null, FlatOrg[]>();
    for (const o of orgs) {
        const effectiveParent = o.parent_id != null && byId.has(o.parent_id) ? o.parent_id : null;
        const list = childrenMap.get(effectiveParent) ?? [];
        list.push(o);
        childrenMap.set(effectiveParent, list);
    }

    for (const list of childrenMap.values()) {
        list.sort((a, b) => {
            if (pinFirst) {
                if (a.name === pinFirst && b.name !== pinFirst) return -1;
                if (b.name === pinFirst && a.name !== pinFirst) return 1;
            }
            return a.name.localeCompare(b.name);
        });
    }

    const result: OrgOption[] = [];

    function walk(parentId: number | null, depth: number) {
        for (const k of childrenMap.get(parentId) ?? []) {
            const hasChildren = (childrenMap.get(k.id) ?? []).length > 0;
            result.push({id: k.id, name: k.name, depth, hasChildren});
            walk(k.id, depth + 1);
        }
    }

    if (root) {
        const hasChildren = (childrenMap.get(root.id) ?? []).length > 0;
        result.push({id: root.id, name: byId.get(root.id)?.name ?? root.name, depth: 0, hasChildren});
        walk(root.id, 1);
        return result;
    }

    walk(null, 0);
    return result;
}

interface Props {
    value: number | null;
    onChange: (id: number | null) => void;
    label?: string;
    placeholder?: string;
    size?: "small" | "medium";
    disabled?: boolean;
    /** Restrict options to this org and its descendants. The org itself is always listed,
     *  even when it isn't a root of (or present in) the user's permitted-org tree. */
    root?: {id: number; name: string};
    /** Org name to float to the top of each level (e.g. "Motimatic"), then A–Z. */
    pinFirst?: string;
}

export default function OrganizationPicker({
                                               value,
                                               onChange,
                                               label,
                                               placeholder = "Search organizations...",
                                               size = "small",
                                               disabled = false,
                                               root,
                                               pinFirst
                                           }: Props) {
    const appSnap = useSnapshot(appStore);

    const options = useMemo(() => {
        const perms = appSnap.loggedInUser?.permissions?.organizations ?? [];
        const flat: FlatOrg[] = perms.map((o: any) => ({
            id: o.id,
            name: o.name,
            parent_id: o.parent_id ?? null,
        }));
        const orgOptions = buildOrgOptions(flat, root, pinFirst);
        return orgOptions
    }, [appSnap.loggedInUser?.permissions?.organizations, root?.id, root?.name, pinFirst]);

    const selected = options.find((o) => o.id === value) ?? null;

    return (
        <Autocomplete
            size={size}
            disabled={disabled}
            options={options}
            getOptionLabel={(opt) => opt.name}
            value={selected}
            onChange={(_e, opt) => onChange(opt ? opt.id : null)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            filterOptions={(opts, state) => {
                const input = state.inputValue.toLowerCase();
                if (!input) return opts;
                const matchingIds = new Set<number>();
                for (let i = 0; i < opts.length; i++) {
                    if (opts[i].name.toLowerCase().includes(input)) {
                        matchingIds.add(i);
                        let depth = opts[i].depth;
                        for (let j = i - 1; j >= 0 && depth > 0; j--) {
                            if (opts[j].depth < depth) {
                                matchingIds.add(j);
                                depth = opts[j].depth;
                            }
                        }
                    }
                }
                return opts.filter((_, idx) => matchingIds.has(idx));
            }}
            renderOption={(props, opt) => (
                <Box
                    component="li"
                    {...props}
                    key={opt.id}
                    sx={{
                        pl: `${16 + opt.depth * 20}px !important`,
                        fontWeight: opt.hasChildren ? 600 : 400,
                        fontSize: "0.875rem",
                    }}
                >
                    {opt.name}
                </Box>
            )}
            renderInput={(params) => (
                <TextField {...params} label={label} placeholder={placeholder} variant="outlined"/>
            )}
        />
    );
}
